import React, { useState } from 'react';
import { projects as personalProjects, commercialProjects } from '../../portfolio';
import { ExternalLink } from 'lucide-react';

const Portfolio = () => {
  const [activeTab, setActiveTab] = useState('Personal');
  const [activeProjectId, setActiveProjectId] = useState(0);

  const projects = activeTab === 'Personal' ? personalProjects : commercialProjects;
  const activeProject = projects[activeProjectId] || projects[0];

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    setActiveProjectId(0);
  };

  const getProjLogo = (name, fallback) => {
    if (name.includes('Tourmoo')) return '/images/trustedby/tourmoologo (1).png';
    if (name.includes('Locomoo')) return '/images/trustedby/locomoo.png';
    if (name.includes('PicBee')) return '/images/trustedby/picbee.png';
    return `/${fallback || name.split(' - ')[0].toLowerCase().replace(/\s+/g, '')}.png`;
  };

  return (
    <>
      <div className="section-header" style={{ marginBottom: '40px' }}>
        <h1 className="section-title">Portfolio</h1>
      </div>
      
      <div className="split-layout">
        <div className="split-sidebar">
          <div style={{ display: 'flex', width: '100%', background: 'var(--surface)', padding: '4px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', marginBottom: '16px' }}>
            <button 
              style={{ flex: 1, padding: '8px 16px', background: activeTab === 'Personal' ? 'var(--text-1)' : 'transparent', color: activeTab === 'Personal' ? 'var(--bg)' : 'var(--text-2)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 500, fontSize: '13px', transition: 'all 0.2s' }}
              onClick={() => handleTabChange('Personal')}
            >
              Personal
            </button>
            <button 
              style={{ flex: 1, padding: '8px 16px', background: activeTab === 'Commercial' ? 'var(--text-1)' : 'transparent', color: activeTab === 'Commercial' ? 'var(--bg)' : 'var(--text-2)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 500, fontSize: '13px', transition: 'all 0.2s' }}
              onClick={() => handleTabChange('Commercial')}
            >
              Commercial
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {projects.map((proj, idx) => (
              <div 
                key={idx} 
                onClick={() => setActiveProjectId(idx)}
                style={{ 
                  display: 'flex', gap: '16px', alignItems: 'center', padding: '12px', 
                  borderRadius: 'var(--radius)', cursor: 'pointer', 
                  background: activeProject?.name === proj.name ? 'var(--surface-hover)' : 'transparent',
                  transition: 'background 0.2s ease',
                  border: activeProject?.name === proj.name ? '1px solid var(--border)' : '1px solid transparent'
                }}
              >
                <div style={{ width: '40px', height: '40px', borderRadius: '6px', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '12px', color: 'var(--text-1)', flexShrink: 0, overflow: 'hidden' }}>
                  <img src={getProjLogo(proj.name, proj.imageFallback)} alt={proj.name} style={{width: '100%', height: '100%', objectFit: 'contain'}} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                  <span style={{display: 'none'}}>{proj.name.substring(0, 3).toUpperCase()}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-1)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {proj.name.split(' - ')[0]}
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981' }}></span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '170px' }}>
                    {proj.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="split-content" style={{ paddingLeft: '24px' }}>
          {activeProject && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)', fontStyle: 'italic', letterSpacing: '0.05em', marginBottom: '32px', textTransform: 'uppercase' }}>
                {activeTab} PROJECT
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                  <div style={{ width: '64px', height: '64px', borderRadius: '12px', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '18px', color: 'var(--text-1)', flexShrink: 0, overflow: 'hidden' }}>
                    <img src={getProjLogo(activeProject.name, activeProject.imageFallback)} alt={activeProject.name} style={{width: '100%', height: '100%', objectFit: 'contain'}} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                    <span style={{display: 'none'}}>{activeProject.name.substring(0, 3).toUpperCase()}</span>
                  </div>
                  <div>
                    <h1 style={{ fontSize: '24px', fontWeight: 600, color: 'var(--text-1)', marginBottom: '8px' }}>{activeProject.name.split(' - ')[0]}</h1>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '12px', color: 'var(--text-3)' }}>
                       <span style={{ padding: '2px 8px', background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', borderRadius: '4px', fontWeight: 500 }}>Active</span>
                       {activeProject.stack && <span>{activeProject.stack.slice(0, 2).join(', ')}</span>}
                    </div>
                  </div>
                </div>

                {activeProject.livePreview && (
                  <a href={activeProject.livePreview} target="_blank" rel="noopener noreferrer" className="btn" style={{ gap: '8px' }}>
                     Open App <ExternalLink size={14} />
                  </a>
                )}
              </div>

              <a href={activeProject.livePreview || '#'} target={activeProject.livePreview ? "_blank" : "_self"} rel="noopener noreferrer" style={{ display: 'block', width: '100%', backgroundColor: "white", aspectRatio: '16/9', background: activeProject.name.match(/Tourmoo|Locomoo|PicBee/) ? 'var(--surface)' : 'var(--border)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', marginBottom: '40px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', textDecoration: 'none' }}>
                 <div style={{ position: 'absolute', color: 'var(--text-3)', zIndex: 0,}}>Logo</div>
                 <img 
                    key={activeProject.name}
                    src={`/${activeProject.imageFallback || activeProject.name.split(' - ')[0].toLowerCase().replace(/\s+/g, '')}.jpg`} 
                    onError={(e) => {
                      if (!e.target.dataset.failed) {
                         e.target.dataset.failed = 'true';
                         e.target.src = getProjLogo(activeProject.name, activeProject.imageFallback);
                         e.target.style.objectFit = 'contain';
                         e.target.style.padding = '32px';
                      } else {
                         e.target.style.display = 'none';
                      }
                    }} 
                    alt="Preview" 
                    style={{width: '100%', height: '100%', objectFit: 'cover', zIndex: 1, position: 'relative'}} 
                 />
              </a>

              <div style={{ fontSize: '15px', color: 'var(--text-2)', lineHeight: 1.7, marginBottom: '48px' }}>
                <p style={{ marginBottom: '24px' }}>{activeProject.description}</p>
                
                <h3 style={{ fontSize: '16px', color: 'var(--text-1)', fontWeight: 600, marginBottom: '16px', marginTop: '32px' }}>How it works</h3>
                <p>
                  Built utilizing {activeProject.stack?.join(', ')}, optimizing workflows and accelerating deployment speeds across cloud infrastructures.
                </p>
              </div>

            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Portfolio;
